/**
 * Onboarding — shown ONCE to new users (when profile has no display_name).
 *
 * Screen 1: "What this is" — removes pressure, builds safety.
 * Screen 2: Name collection — personalises every Claude interaction.
 * Screen 3: Birthday (month + day only) — for future astrology features.
 *
 * Saves display_name, birth_month, birth_day to inner_awakening.profiles.
 * Parent removes this overlay once onComplete() is called.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { db } from '../../lib/supabase'

function OnboardingSpiral() {
  const cx = 28, cy = 28, rMax = 22, rMin = 4, turns = 1.5, n = 200
  const pts: string[] = []
  for (let i = 0; i <= n; i++) {
    const t = i / n
    const theta = t * turns * 2 * Math.PI
    const r = rMax + (rMin - rMax) * t
    const x = cx + r * Math.cos(theta - Math.PI / 2)
    const y = cy + r * Math.sin(theta - Math.PI / 2)
    pts.push(`${i === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`)
  }
  return (
    <div className="flex justify-center mb-8">
      <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
        <polyline points={pts.map(p => p.replace(/[ML] /, '')).join(' ')}
          stroke="#9B72C0" strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.7" />
      </svg>
    </div>
  )
}

interface OnboardingProps {
  userId: string
  onComplete: (firstName: string) => void
}

type Screen = 'what-this-is' | 'your-name' | 'your-birthday' | 'body-checkin'

function daysInMonth(month: number): number {
  // Use a non-leap year; day count is for display only
  return new Date(2001, month, 0).getDate()
}

export default function Onboarding({ userId, onComplete }: OnboardingProps) {
  const { t, i18n } = useTranslation()

  /*
   * The four grounding cues shown before the first session. Length-guarded so a
   * malformed locale cannot render an empty list on the screen whose entire
   * purpose is to slow someone down before they begin.
   */
  const groundingSteps = (() => {
    const fallback = [
      'Feel your feet on the ground.',
      'Unclench your jaw. Drop your shoulders.',
      'Take one slow breath — in through your nose, out through your mouth.',
      'Notice where you are right now. You are safe.',
    ]
    const v = t('onboarding.groundingSteps', { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  })()

  /*
   * The three lines describing what a session is. Length-guarded for the same
   * reason as the grounding cues above — a malformed locale falls back to the
   * full English set rather than rendering a partial list.
   */
  const introLines = (() => {
    const fallback = [
      "You'll name what you're feeling and write about what happened.",
      "Then we'll look at what lies deeper — the pattern beneath the event.",
      "This is a space for reflection, beyond advice and diagnosis. It's you and a process developed over decades.",
    ]
    const v = t('onboarding.intro', { returnObjects: true })
    return Array.isArray(v) && v.length === fallback.length ? (v as string[]) : fallback
  })()

  const [screen, setScreen] = useState<Screen>('what-this-is')
  const [name, setName] = useState('')
  const [birthMonth, setBirthMonth] = useState<number>(0)   // 0 = not selected
  const [birthDay, setBirthDay] = useState<number>(0)
  const [birthYear, setBirthYear] = useState<number>(0)     // 0 = not selected
  const [saving, setSaving] = useState(false)

  const currentYear = new Date().getFullYear()
  const years = Array.from({ length: 100 }, (_, i) => currentYear - 10 - i)
  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i, 1).toLocaleDateString(i18n.language, { month: 'long' })
  )

  const days = birthMonth > 0 ? daysInMonth(birthMonth) : 31

  async function handleNameContinue() {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setScreen('your-birthday')
  }

  async function handleBirthdayComplete(skip = false) {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setSaving(true)

    /*
     * This upsert is what CREATES the profile row, so it is the first chance to
     * record a language, and `profiles.language` is NULL until something does
     * (#35). Store the language these screens were actually rendered in: the
     * seeker read them and continued, which is a stronger signal than a browser
     * setting. Base code only -- the catalogue is keyed 'pl', never 'pl-PL'.
     */
    const update: Record<string, unknown> = {
      id: userId,
      display_name: trimmed,
      language: (i18n.language || 'en').split('-')[0],
    }
    if (!skip && birthMonth > 0 && birthDay > 0) {
      update.birth_month = birthMonth
      update.birth_day = birthDay
      if (birthYear > 0) update.birth_year = birthYear
    }

    await db()
      .from('profiles')
      .upsert(update, { onConflict: 'id' })

    setSaving(false)
    setScreen('body-checkin')
  }

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col items-center justify-center px-6"
      style={{ background: '#FAF7F4' }}
    >
      <div className="w-full max-w-sm page-enter">

        {/* Screen 1 — What this is */}
        {screen === 'what-this-is' && (
          <div className="flex flex-col">
            <OnboardingSpiral />

            <p className="text-xs font-medium uppercase tracking-widest mb-5 text-center"
              style={{ color: '#9B72C0' }}>
              {t('onboarding.beforeYouBegin')}
            </p>

            <h1 className="font-display text-2xl text-center mb-8 leading-snug"
              style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('onboarding.guidedSession1')}<br />{t('onboarding.guidedSession2')}
            </h1>

            <div className="space-y-4 mb-10">
              {[
                ...introLines,
                t('onboarding.takeYourTime'),
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0"
                    style={{ background: '#9B72C0' }} />
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{line}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => setScreen('your-name')}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('onboarding.soundsGood')}
            </button>
          </div>
        )}

        {/* Screen 2 — Name */}
        {screen === 'your-name' && (
          <div className="flex flex-col message-appear">
            <OnboardingSpiral />
            <p className="text-xs font-medium uppercase tracking-widest mb-5 text-center"
              style={{ color: '#9B72C0' }}>
              {t('onboarding.quickIntroduction')}
            </p>

            <h2 className="font-display text-2xl text-center mb-3 leading-snug"
              style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('onboarding.whatCallYou')}
            </h2>

            <p className="text-sm text-center mb-8" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
              {t('onboarding.justFirstName')}
            </p>

            {/* Purple card wrapper — Onboarding phase accent (#9B72C0) at 4% tint */}
            <div className="rounded-2xl px-5 py-5 mb-6" style={{
              background: 'rgba(155,114,192,0.04)',
              borderTop: '3px solid #9B72C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                {t('onboarding.yourName')}
              </p>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleNameContinue() }}
                placeholder={t('onboarding.firstNamePlaceholder')}
                maxLength={100}
                autoFocus
                className="w-full px-4 py-4 rounded-xl border text-base text-center focus:outline-none"
                style={{
                  // Border is always the phase accent purple
                  borderColor: '#9B72C0',
                  background: 'white',
                  color: '#1C1128',
                  fontSize: '1.1rem',
                }}
                onFocus={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = '0 0 0 3px rgba(155,114,192,0.12)' }}
                onBlur={e => { e.target.style.borderColor = '#9B72C0'; e.target.style.boxShadow = 'none' }}
              />
            </div>

            <button
              onClick={handleNameContinue}
              disabled={!name.trim()}
              className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
              style={{
                background: name.trim() ? '#3D1F62' : '#E4D5EE',
                color: name.trim() ? 'white' : '#9B8FAA',
                border: 'none',
                cursor: name.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              {t('session.continue')}
            </button>
            <button
              onClick={() => onComplete('')}
              className="w-full py-3 rounded-xl text-sm"
              style={{ background: 'transparent', border: 'none', color: '#9B8FAA', cursor: 'pointer' }}
            >
              {t('common.skip')}
            </button>
          </div>
        )}

        {/* Screen 4 — Body Check-in */}
        {screen === 'body-checkin' && (
          <div className="flex flex-col message-appear">
            <OnboardingSpiral />
            <p className="text-xs font-medium uppercase tracking-widest mb-5 text-center"
              style={{ color: '#9B72C0' }}>
              {t('onboarding.beforeYourSession')}
            </p>

            <h2 className="font-display text-2xl text-center mb-3 leading-snug"
              style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('onboarding.takeOneBreath')}
            </h2>

            <p className="text-sm text-center mb-8" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
              {t('onboarding.workAsksSomething')}
            </p>

            <div
              className="flex items-center justify-center mb-8"
            >
              <div style={{ position: 'relative', width: 80, height: 80 }}>
                <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(155,114,192,0.18) 0%, rgba(155,114,192,0) 70%)',
                  animation: 'breathe 4s ease-in-out infinite',
                }} />
                <div style={{
                  position: 'absolute', inset: '16px', borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(155,114,192,0.35) 0%, rgba(155,114,192,0.1) 70%)',
                  animation: 'breathe 4s ease-in-out infinite 0.5s',
                }} />
                <div style={{
                  position: 'absolute', inset: '28px', borderRadius: '50%',
                  background: '#9B72C0',
                  opacity: 0.5,
                  animation: 'breathe 4s ease-in-out infinite 1s',
                }} />
                <style>{`
                  @keyframes breathe {
                    0%, 100% { transform: scale(0.92); opacity: 0.6; }
                    50% { transform: scale(1.08); opacity: 1; }
                  }
                `}</style>
              </div>
            </div>

            <div className="space-y-3 mb-10">
              {groundingSteps.map((line, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: '#9B72C0' }} />
                  <p className="text-sm leading-relaxed" style={{ color: '#6B5F7A' }}>{line}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => onComplete(name.trim())}
              className="w-full py-4 rounded-xl font-medium tracking-wide"
              style={{ background: '#3D1F62', color: 'white', border: 'none', cursor: 'pointer' }}
            >
              {t('onboarding.imHereBegin')}
            </button>
          </div>
        )}

        {/* Screen 3 — Birthday */}
        {screen === 'your-birthday' && (
          <div className="flex flex-col message-appear">
            <OnboardingSpiral />
            <p className="text-xs font-medium uppercase tracking-widest mb-5 text-center"
              style={{ color: '#9B72C0' }}>
              {t('onboarding.oneMoreThing')}
            </p>

            <h2 className="font-display text-2xl text-center mb-3 leading-snug"
              style={{ color: '#1C1128', fontWeight: 400 }}>
              {t('onboarding.whensBirthday')}
            </h2>

            <p className="text-sm text-center mb-8" style={{ color: '#6B5F7A', lineHeight: 1.6 }}>
              {t('onboarding.fullBirthday')}<br />
              {t('onboarding.personalisedInsights')}
            </p>

            {/* Purple card wrapper — Onboarding phase accent (#9B72C0) at 4% tint */}
            <div className="rounded-2xl px-5 py-5 mb-6" style={{
              background: 'rgba(155,114,192,0.04)',
              borderTop: '3px solid #9B72C0',
              borderRight: '1.5px solid #E4D5EE',
              borderBottom: '1.5px solid #E4D5EE',
              borderLeft: '1.5px solid #E4D5EE',
            }}>
              <p className="text-xs font-medium uppercase tracking-widest mb-3" style={{ color: '#9B72C0' }}>
                {t('onboarding.yourBirthday')}
              </p>
              <div className="flex gap-2">
              {/* Month */}
              <div className="flex-1">
                <label className="block text-xs font-medium mb-2 text-center" style={{ color: '#6B5F7A' }}>
                  {t('profile.birthMonth')}
                </label>
                <select
                  value={birthMonth}
                  onChange={e => { setBirthMonth(Number(e.target.value)); setBirthDay(0) }}
                  className="w-full px-2 py-3 rounded-xl border text-sm focus:outline-none"
                  style={{
                    borderColor: birthMonth ? '#9B72C0' : '#E4D5EE',
                    background: 'white',
                    color: birthMonth ? '#1C1128' : '#C4B5D4',
                    cursor: 'pointer',
                    appearance: 'none',
                    textAlign: 'center',
                  }}
                >
                  <option value={0} disabled>{t('profile.birthMonth')}</option>
                  {months.map((m, i) => (
                    <option key={m} value={i + 1}>{m}</option>
                  ))}
                </select>
              </div>

              {/* Day */}
              <div style={{ width: '72px' }}>
                <label className="block text-xs font-medium mb-2 text-center" style={{ color: '#6B5F7A' }}>
                  {t('profile.birthDay')}
                </label>
                <select
                  value={birthDay}
                  onChange={e => setBirthDay(Number(e.target.value))}
                  disabled={birthMonth === 0}
                  className="w-full px-2 py-3 rounded-xl border text-sm focus:outline-none"
                  style={{
                    borderColor: birthDay ? '#9B72C0' : '#E4D5EE',
                    background: 'white',
                    color: birthDay ? '#1C1128' : '#C4B5D4',
                    cursor: birthMonth ? 'pointer' : 'not-allowed',
                    appearance: 'none',
                    textAlign: 'center',
                    opacity: birthMonth === 0 ? 0.5 : 1,
                  }}
                >
                  <option value={0} disabled>{t('profile.birthDay')}</option>
                  {Array.from({ length: days }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              {/* Year */}
              <div style={{ width: '96px' }}>
                <label className="block text-xs font-medium mb-2 text-center" style={{ color: '#6B5F7A' }}>
                  {t('profile.year')}
                </label>
                <select
                  value={birthYear}
                  onChange={e => setBirthYear(Number(e.target.value))}
                  className="w-full px-2 py-3 rounded-xl border text-sm focus:outline-none"
                  style={{
                    borderColor: birthYear ? '#9B72C0' : '#E4D5EE',
                    background: 'white',
                    color: birthYear ? '#1C1128' : '#C4B5D4',
                    cursor: 'pointer',
                    appearance: 'none',
                    textAlign: 'center',
                  }}
                >
                  <option value={0} disabled>{t('profile.year')}</option>
                  {years.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
              </div>
            </div>

            <button
              onClick={() => handleBirthdayComplete(false)}
              disabled={!birthMonth || !birthDay || !birthYear || saving}
              className="w-full py-4 rounded-xl font-medium tracking-wide mb-3"
              style={{
                background: birthMonth && birthDay && birthYear ? '#3D1F62' : '#E4D5EE',
                color: birthMonth && birthDay && birthYear ? 'white' : '#9B8FAA',
                border: 'none',
                cursor: birthMonth && birthDay && birthYear ? 'pointer' : 'not-allowed',
              }}
            >
              {saving ? t('common.saving') : t('onboarding.imReady')}
            </button>
            <button
              onClick={() => handleBirthdayComplete(true)}
              disabled={saving}
              className="w-full py-3 rounded-xl text-sm"
              style={{ background: 'transparent', border: 'none', color: '#9B8FAA', cursor: 'pointer' }}
            >
              {t('onboarding.skipForNow')}
            </button>
          </div>
        )}

      </div>
    </div>
  )
}
